import { createAsyncThunk, createSlice, Dispatch } from "@reduxjs/toolkit";
import { openDocument } from "../../../utils/helpers/OpenDocumentHelper";
import {
  cloneDocumentPart,
  detachMultilinkedDocumentPart,
  downloadDocumentCopy,
  duplicateDocumentRoot,
  exportDocumentRoot,
  exportWorkflowHistory,
  fetchCreatedDocument,
  importDocumentRoot,
} from "../../../api/document/Documents";
import {
  alignDocumentPart,
  checkDifferences,
  deleteDocumentPart,
  editDocumentPart,
  fetchChangeDocumentPartStructure,
  unlinkDocumentPart,
} from "../../../api/document/DocumentsPart";
import { DEFAULT_FOLDER_CREATION } from "../../../components/document/WithApiFileLocationProvider";
import {
  CLONE_ELABORATION,
  DELETE_OBJECT_NOTIFICATION,
  EDIT_DOCUMENT_PROPERTIES_NOTIFICATION,
  INSERT_OBJECT_NOTIFICATION,
} from "../../../utils/constants/notification";
import { deleteXMLPart, DocPropKeys, retrieveDocumentProp } from "../../../utils/helpers/CustomXmlHelper";
import {
  deleteContentControlById,
  getContentControlId,
  getContentControlWithDocPartId,
} from "../../../utils/helpers/ContentControlHelper";
import { normalizeDocumentName } from "../../../utils/helpers/ObjectCheckerHelper";
import NotificationManager from "../../../common/notification/NotificationManager";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";
import { DocumentPart } from "../../../api/models";
import { TgkImpExpElaborationRequest } from "../../../api/models/tgk-imp-exp-elaboration-request";
import * as uuid from "uuid";
import { TgkDuplicateElaborationRequest } from "../../../api/models/tgk-duplicate-elaboration-request";
import { TgkChangeDocStructureElaborationRequest } from "../../../api/models/tgk-change-doc-structure-elaboration-request";
import { StructureDocumentPart } from "../../../api/models/structure-document-part.ts";
import { store } from "../../store/store";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../../utils/constants/eventChannel";
import { DocumentInsertionPoint, insertDocumentPart } from "../../../office/WordDocumentPart";
import { selectCpmUsername, setIxbrlSettings } from "../settings/configurations-slice";
import { fetchIxbrlSettings, fetchUpdateIxbrlSettings } from "../../../pages/seahorse/SeahorseUtils";
import { DimensionParameter } from "../../../api/models/dimension-parameter";
import { TemplateInfo } from "../../../api/models/template-info";
import { CoObjectAdvanced, CoObjectItem } from "../../../components/interfaces/CoObject";
import { XmlDocumentMetadata } from "../../../api/models/xml-document-metadata";
import { DownloadDocument } from "../../../api/models/download-document";
import { TgkDocumentElaborationRequest } from "../../../api/models/tgk-document-elaboration-request";
import { TgkCloneElaborationRequest } from "../../../api/models/tgk-clone-elaboration-request";
import { TgkDetachElaborationRequest } from "../../../api/models/tgk-detach-elaboration-request";
import { DocumentPartSettings } from "../../../api/models/document-part-settings";

import { DocumentItem } from "../../../api/models/document-item";

export type { DocumentItem };

const documentSlice = createSlice({
  name: "document",
  initialState: {},
  reducers: {
    // your reducers here
  },
});

export default documentSlice.reducer; // <-- Important: default export the reducer

export const fetchCreateDocumentAsync = createAsyncThunk<
  DocumentItem,
  {
    documentName: string;
    documentDescription: string;
    path: string;
    metadata: DocumentPartMetadata;
    settings: DocumentPartSettings;
  }
>("document/fetchCreateDocumentAsync", async (input) => {
  let request: DocumentPart = {
    name: normalizeDocumentName(input.documentName),
    description: input.documentDescription,
    path: normalizePath(input.path),
    metadata: input.metadata,
    settings: input.settings,
  };
  const documentItem: DocumentItem = await fetchCreatedDocument(request);
  if (documentItem.id) {
    const ixbrlEnabled = input.settings.isIxbrlEnabled ?? false;
    fetchUpdateIxbrlSettings(documentItem.id, ixbrlEnabled).then(() => {
      fetchIxbrlSettings().then((result) => store.dispatch(setIxbrlSettings(result)));
    });
    //store.dispatch(setIxbrlEnabled(false));
  }

  openDocument(documentItem.wordAddress);
  return documentItem;
});

export const editDocumentMetadataAsync = async (
  tgkDocumentElaborationRequest: TgkDocumentElaborationRequest,
  mustAlignChildren: boolean
) => {
  try {
    const xmlDocumentMetadata: XmlDocumentMetadata = await editDocumentPart(tgkDocumentElaborationRequest);
    // TODO this is not needed anymore, we send it from the backend
    if (!mustAlignChildren) {
      NotificationManager.addNotification({
        key: EDIT_DOCUMENT_PROPERTIES_NOTIFICATION,
        message: formatMessage("cof.documentproperties.message"),
        title: null,
        type: "success",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    }

    return xmlDocumentMetadata;
  } catch (e) {
    console.log(e);
  }
};

export const alignDocumentMetadataAsync = async (tgkDocumentElaborationRequest: TgkDocumentElaborationRequest) => {
  try {
    await alignDocumentPart(tgkDocumentElaborationRequest);
  } catch (e) {
    console.log(e);
  }
};

export const checkDifferencesAsync = async (tgkDocumentElaborationRequest: TgkDocumentElaborationRequest) => {
  try {
    await checkDifferences(tgkDocumentElaborationRequest);
  } catch (e) {
    console.log(e);
  }
};

export const unlinkDocumentPartAsync = async (input: { docPartFatherId: string; unlinkedDocPartId: string }) => {
  try {
    let unlinkedCoObject: CoObjectItem = await getContentControlWithDocPartId(input.unlinkedDocPartId);

    await unlinkDocumentPart(input.docPartFatherId, input.unlinkedDocPartId, unlinkedCoObject?.id);
    if (unlinkedCoObject) {
      await deleteXMLPart(unlinkedCoObject.contentControlId);
      await deleteContentControlById(unlinkedCoObject.contentControlId);
    } else {
      console.warn(
        "Unlinked document part does not have an associated content control, skipping deletion of content control and custom xml part"
      );
    }

    NotificationManager.addNotification({
      key: INSERT_OBJECT_NOTIFICATION,
      message: formatMessage("cof.createnewdocument.message.unlinksuccess"),
      title: null,
      type: "success",
      closable: true,
      removeOnClose: true,
      isTemporary: true,
    });
  } catch (e) {
    console.log(e);
  }
};

export const deleteDocumentPartAsync = async (input: {
  docPartId: string;
  docPartDriveItemId: string;
  openedFatherDocumentPartId?: string;
  isRoot: boolean;
  deleteFromClient?: boolean;
}) => {
  try {
    await deleteDocumentPart(
      input.docPartId,
      input.docPartDriveItemId,
      input.openedFatherDocumentPartId,
      input.deleteFromClient
    );

    if (!input.isRoot) {
      let contentControlId: number = await getContentControlId(input.docPartId);
      await deleteContentControlById(contentControlId);
    }

    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.createnewdocument.message.deletesuccess"),
      title: null,
      type: "success",
      closable: true,
      removeOnClose: true,
      isTemporary: true,
    });
  } catch (e) {
    console.log(e);
  }
};

export function mapTgkChangeStructureElaborationRequest(data: {
  structureDocumentPart: StructureDocumentPart;
  correlationId: string;
  userId: string;
  documentId: string;
  channelIdToNotify: string;
}): TgkChangeDocStructureElaborationRequest {
  return {
    channelIdToNotify: data.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
    clientId: store.getState().configurationState.clientId,
    correlationId: data.correlationId,
    userId: data.userId,
    structureDocumentPart: data.structureDocumentPart,
    typeElaborationRequest: "tgkChangeDocStructureElaborationRequest",
    documentId: data.documentId,
  };
}

export function mapTgkDuplicateElaborationRequest(data: {
  userId: string;
  documentId: string;
  sharepointPath: string;
  tenantId: string;
  siteId: string;
  correlationId: string;
  documentName: string;
  description: string;
  documentPartMetadata: DimensionParameter[];
  advanced: CoObjectAdvanced;
  language: string;
  channelIdToNotify: string;
  duplicateAttachments: boolean;
  copySharePointUserRights?: boolean;
}): TgkDuplicateElaborationRequest {
  return {
    customerAzureTenantId: data.tenantId,
    channelIdToNotify: data.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
    clientId: store.getState().configurationState.clientId,
    correlationId: data.correlationId,
    userId: data.userId,
    documentId: data.documentId,
    sharepointPath: data.sharepointPath,
    siteId: data.siteId,
    contentControlId: null,
    documentName: data.documentName,
    additionalInfo: data.description,
    documentPartMetadata: {
      documentId: data.documentId,
      parametersMetadata: data.documentPartMetadata,
      formatMetadata: data.advanced.formatMetadata,
      formatImage: data.advanced.formatImage,
      templateInfo: data.advanced.templateInfo,
      language: data.language,
    },
    typeElaborationRequest: "tgkDuplicateElaborationRequest",
    duplicateAttachments: data.duplicateAttachments,
    copySharePointUserRights: data.copySharePointUserRights,
  };
}

export function mapTgkImpExpElaborationRequest(data: {
  userId: string;
  documentId: string;
  channelIdToNotify: string;
  driveItemId: string;
  sharepointPath: string;
  elaborationType: "IMPORT" | "EXPORT";
  tenantId: string;
  siteId: string;
  zipFileName: string;
  zipFilePath: string;
  clientId: string;
  correlationId: string;
}): TgkImpExpElaborationRequest {
  return {
    customerAzureTenantId: data.tenantId,
    clientId: store.getState().configurationState.clientId,
    correlationId: data.correlationId,
    userId: data.userId,
    documentId: data.documentId,
    channelIdToNotify: data.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
    driveItemId: data.driveItemId,
    sharepointPath: data.sharepointPath,
    elaborationType: data.elaborationType,
    siteId: data.siteId,
    zipFileName: data.zipFileName,
    zipFilePath: data.zipFilePath,
    contentControlId: null,
    typeElaborationRequest: "tgkImpExpElaborationRequest",
  };
}

export const exportDocumentRootAsync = async (input: {
  channelIdToNotify: string;
  documentId: string;
  username: string;
  tenantId: string;
  sharepointPath: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  clientId: string;
}) => {
  try {
    await exportDocumentRoot(
      mapTgkImpExpElaborationRequest({
        userId: input.username,
        documentId: input.documentId,
        channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
        driveItemId: null,
        sharepointPath: input.sharepointPath,
        elaborationType: "EXPORT",
        tenantId: input.tenantId,
        siteId: null,
        zipFileName: null,
        zipFilePath: null,
        clientId: input.clientId,
        correlationId: uuid.v4(),
      })
    );
  } catch {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.elaboration.error.export.message"),
      title: "",
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

export const changeDocumentPartStructureAsync = async (input: {
  username: string;
  structureDocumentPart: StructureDocumentPart;
  documentId: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  clientId: string;
  channelIdToNotify: string;
}) => {
  try {
    await fetchChangeDocumentPartStructure(
      mapTgkChangeStructureElaborationRequest({
        channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
        userId: input.username,
        correlationId: uuid.v4(),
        structureDocumentPart: input.structureDocumentPart,
        documentId: input.documentId,
      })
    );
  } catch {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.elaboration.changedocumentstructure.message.failed"),
      title: formatMessage("cof.elaboration.changedocumentstructure.title.failed"),
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

export const duplicateDocumentRootAsync = async (input: {
  documentId: string;
  channelIdToNotify: string;
  username: string;
  tenantId: string;
  sharepointPath: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  documentPartMetadata: DimensionParameter[];
  documentName: string;
  description: string;
  advanced: CoObjectAdvanced;
  language: string;
  clientId: string;
  duplicateAttachments: boolean;
  copySharePointUserRights?: boolean;
}) => {
  try {
    await duplicateDocumentRoot(
      mapTgkDuplicateElaborationRequest({
        userId: input.username,
        documentId: input.documentId,
        sharepointPath: input.sharepointPath,
        tenantId: input.tenantId,
        siteId: null,
        correlationId: uuid.v4(),
        documentPartMetadata: input.documentPartMetadata,
        documentName: input.documentName,
        description: input.description,
        advanced: input.advanced,
        language: input.language,
        channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
        duplicateAttachments: input.duplicateAttachments,
        copySharePointUserRights: input.copySharePointUserRights,
      })
    );
  } catch {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.elaboration.error.duplicate.message"),
      title: "",
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

export const importDocumentRootAsync = async (input: {
  username: string;
  tenantId: string;
  sharepointPath: string;
  zipFileName: string;
  zipFilePath: string;
  driveItemId: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  channelIdToNotify?: string;
  siteId: string;
  clientId: string;
}) => {
  const correlationId = uuid.v4();
  try {
    await importDocumentRoot(
      mapTgkImpExpElaborationRequest({
        userId: input.username,
        documentId: correlationId,
        channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
        driveItemId: input.driveItemId,
        sharepointPath: input.sharepointPath,
        zipFileName: input.zipFileName,
        zipFilePath: input.zipFilePath,
        elaborationType: "IMPORT",
        tenantId: input.tenantId,
        siteId: input.siteId,
        clientId: input.clientId,
        correlationId: correlationId,
      })
    );
  } catch {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.elaboration.error.import.message"),
      title: "",
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

export function getFormattedPath(input: string) {
  if (input) return input.replace(/>/g, "/");
  else return null;
}

export function normalizePath(path: string) {
  if (!path?.length) return "";
  return path
    .replace(DEFAULT_FOLDER_CREATION + ">", "")
    .replace(/[<>\\]/g, "/")
    .replace(/\/+/g, "/")
    .replace(/\/$/g, "")
    .trim();
}

export const detachMultilinkedDocumentPartAsync = async (input: {
  createContentControl: boolean;
  channelIdToNotify: string;
  documentId: string;
  documentName: string;
  fatherDocumentId: string;
  documentPartMetadata: DimensionParameter[];
  username: string;
  tenantId: string;
  sharepointPath: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  clientId: string;
  language: string;
  templateInfo: TemplateInfo;
  locale: string;
}) => {
  let createdContentControl: Awaited<ReturnType<typeof insertDocumentPart>> | undefined = undefined;
  try {
    const detachedObject: CoObjectItem = await getContentControlWithDocPartId(input.documentId);
    createdContentControl = input.createContentControl
      ? await insertDocumentPart(input.documentName, {
          referenceContentControlId: detachedObject.contentControlId,
          referenceDocumentId: input.fatherDocumentId,
          position: "after",
        })
      : undefined;

    const detachRequest = buildDetachElaborationRequest({
      ...input,
      fatherDocumentId: input.fatherDocumentId,
      contentControlId: createdContentControl?.id?.toString(),
      contentControlIdToBeDetached: detachedObject.contentControlId?.toString(),
    });

    await detachMultilinkedDocumentPart(detachRequest);
  } catch {
    // Clean up orphaned Content Control created before the failed API call
    if (createdContentControl) {
      try {
        await deleteContentControlById(createdContentControl.id, false);
      } catch (cleanupError) {
        console.error("Failed to cleanup orphaned Content Control after detach failure", cleanupError);
      }
    }
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.elaboration.error.detach.message"),
      title: "",
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

const buildDetachElaborationRequest = (
  input: Parameters<typeof buildDuplicateElaborationRequest>[0] & {
    fatherDocumentId: string;
    contentControlIdToBeDetached: string;
  }
): TgkDetachElaborationRequest => {
  return {
    ...buildDuplicateElaborationRequest(input),
    fatherDocumentId: input.fatherDocumentId,
    contentControlIdToBeDetached: input.contentControlIdToBeDetached,
    elaborationType: "DETACH",
    typeElaborationRequest: "tgkDetachElaborationRequest",
  };
};

export const cloneDocumentPartAsync = async (input: {
  channelIdToNotify: string;
  documentId: string;
  documentName: string;
  documentNameSuffix: string;
  fatherDocumentId: string;
  documentPartMetadata: DimensionParameter[];
  username: string;
  tenantId: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  clientId: string;
  language: string;
  templateInfo: TemplateInfo;
  locale: string;
}) => {
  try {
    const coObjectItem = await getContentControlWithDocPartId(input.documentId);
    let documentInsertionPoint: DocumentInsertionPoint = {
      // TODO check this, is documentId the right value?
      referenceDocumentId: input.documentId,
      referenceContentControlId: coObjectItem.contentControlId,
      position: "after",
    };

    let createdContentControl = await insertDocumentPart(input.documentName, documentInsertionPoint);

    const cloneRequest = buildCloneElaborationRequest({
      ...input,
      fatherDocumentId: input.fatherDocumentId,
      contentControlId: createdContentControl.id?.toString(),
      documentNameSuffix: input.documentNameSuffix,
    });

    await cloneDocumentPart(cloneRequest);
  } catch {
    NotificationManager.addNotification({
      key: CLONE_ELABORATION,
      message: formatMessage("cof.elaboration.error.clone.message"),
      title: "",
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

const buildCloneElaborationRequest = (
  input: Parameters<typeof buildDuplicateElaborationRequest>[0] & {
    fatherDocumentId: string;
    documentNameSuffix: string;
  }
): TgkCloneElaborationRequest => {
  return {
    ...buildDuplicateElaborationRequest(input),
    fatherDocumentId: input.fatherDocumentId,
    documentNameSuffix: input.documentNameSuffix,
    elaborationType: "CLONE",
    typeElaborationRequest: "tgkCloneElaborationRequest",
  };
};

const buildDuplicateElaborationRequest = (input: {
  channelIdToNotify: string;
  contentControlId?: string;
  documentId: string;
  documentName: string;
  sharepointPath?: string;
  documentPartMetadata: DimensionParameter[];
  username: string;
  tenantId: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  clientId: string;
  language: string;
  templateInfo: TemplateInfo;
  locale: string;
}): TgkDuplicateElaborationRequest => {
  return {
    customerAzureTenantId: input.tenantId,
    // clientId: store.getState().configurationState.clientId,
    clientId: input.clientId,
    correlationId: uuid.v4(),
    userId: input.username,
    documentId: input.documentId,
    documentName: input.documentName,
    channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
    contentControlId: input.contentControlId,
    driveItemId: null,
    sharepointPath: input.sharepointPath,
    documentPartMetadata: {
      documentId: input.documentId,
      parametersMetadata: input.documentPartMetadata,
      formatMetadata: {
        numberFormatValue: null,
        numberType: null,
        percentageFormatValue: null,
        percentageType: null,
        dateFormatValue: null,
        dateType: null,
        locale: input.locale,
      },
      formatImage: null,
      language: input.language,
      templateInfo: input.templateInfo,
    },
    siteId: null,
  };
};

export async function downloadDocumentCopyAsync(data: DownloadDocument) {
  const storeData = store.getState();
  const clientId = storeData.configurationState.clientId;
  const username = selectCpmUsername(storeData);
  const channel = await retrieveDocumentProp(DocPropKeys.DocumentId);

  console.log("channel", channel);

  await downloadDocumentCopy({
    correlationId: uuid.v4(),
    clientId: clientId,
    userId: username,
    typeElaborationRequest: "tgkDownloadCopyElaborationRequest",
    channelIdToNotify: channel || EVENT_CHANNEL_DEFAULT_TOPIC,
    downloadDocument: data,
  });
}

export async function exportWorkflowHistoryAsync(data: DownloadDocument) {
  const storeData = store.getState();
  const clientId = storeData.configurationState.clientId;
  const username = selectCpmUsername(storeData);
  const channel = await retrieveDocumentProp(DocPropKeys.DocumentId);

  console.log("channel", channel);

  await exportWorkflowHistory({
    correlationId: uuid.v4(),
    clientId: clientId,
    userId: username,
    typeElaborationRequest: "tgkExportSubmissionHistoryElaborationRequest",
    channelIdToNotify: channel || EVENT_CHANNEL_DEFAULT_TOPIC,
    downloadDocument: data,
    operationType: "EXPORT_SUBMISSION_HISTORY",
  });
}
